import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiClient from "../../../../helpers/api-client";
import { AssessmentInfo, VerityAssessment } from "@verity/api-client/types";
import { extractAssessmentName } from "../../../../helpers/assessment-name";
import { PortalControllerContext } from "../../../context";

export type TakeAssessmentSessions = Awaited<
	ReturnType<typeof apiClient.proctoringSession.getStudentSubmissionsByAssessment>
>;

export default function useFetchAssessmentInfo(quizId: string | undefined, proctoringSessionId: string | undefined) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [fetching, setFetching] = useState(false);
	const [verityAssessment, setVerityAssessment] = useState<VerityAssessment | undefined>(undefined);
	const [isDeleted, setIsDeleted] = useState<boolean>(false);
	const [lmsQuizId, setLmsQuizId] = useState<string | undefined>(undefined);
	const [quizName, setQuizName] = useState<string>("");
	const [courseName, setCourseName] = useState<string | undefined>(undefined);
	const [sessions, setSessions] = useState<TakeAssessmentSessions>([]);

	const filterSessions = useCallback(
		(sessions: TakeAssessmentSessions) => {
			return sessions.filter((session) => {
				// Keep if has an endTime or is the current session
				return !!session.endTime || session.uuid === proctoringSessionId;
			});
		},
		[proctoringSessionId],
	);

	useEffect(() => {
		if (!quizId) return;
		void (async () => {
			try {
				setFetching(true);
				const numQuizd = parseInt(quizId);
				if (isNaN(numQuizd)) {
					throw new Error("Invalid quizId");
				}

				const [lmsRes, verityRes, sessionRes] = await Promise.all([
					apiClient.assessment.getAssessmentByAssessmentId(numQuizd),
					apiClient.assessment.getVerityAssessment(numQuizd),
					apiClient.proctoringSession.getStudentSubmissionsByAssessment(numQuizd),
				]);

				setVerityAssessment(verityRes ?? undefined);
				setIsDeleted(lmsRes?.isDeleted ?? false);
				setQuizName(extractAssessmentName(lmsRes));
				setLmsQuizId(lmsRes?.quizId);
				setSessions(filterSessions(sessionRes));

				if (lmsRes) {
					const courseInfo = await apiClient.course.getCourseInfoFromId(lmsRes.courseId);
					setCourseName(courseInfo.name ?? undefined);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setFetching(false);
			}
		})();
	}, [quizId, filterSessions]);

	const curSession = useMemo(
		() => sessions.find((session) => session.uuid === proctoringSessionId),
		[proctoringSessionId, sessions],
	);

	const sessionStartTime = useMemo(() => curSession?.startTime ?? undefined, [curSession]);

	const isValidNumAttempts = useMemo(() => {
		if (!verityAssessment) return false;

		if (verityAssessment.numAttempts <= 0) return true; // unlimited attempts

		return sessions.length < verityAssessment.numAttempts;
	}, [verityAssessment, sessions]);

	const assessmentInfo = useMemo((): AssessmentInfo | undefined => {
		if (!lmsQuizId) {
			return undefined;
		}

		return {
			quizId: lmsQuizId,
			courseId: lmsProvidedCourseId,
			lms,
			hostname: instituteIntegrationHostname,
		};
	}, [lmsQuizId, lmsProvidedCourseId, lms, instituteIntegrationHostname]);

	useEffect(() => {
		if (!quizId) return;
		void (async () => {
			try {
				const numQuizd = parseInt(quizId);
				if (isNaN(numQuizd)) {
					throw new Error("Invalid quizId");
				}
				const sessions = await apiClient.proctoringSession.getStudentSubmissionsByAssessment(numQuizd);
				setSessions(filterSessions(sessions));
			} catch (err) {
				console.error(err);
			}
		})();
	}, [filterSessions, proctoringSessionId, quizId]);

	return {
		fetching,
		verityAssessment,
		isDeleted,
		quizName,
		courseName,
		assessmentInfo,
		isValidNumAttempts,
		sessionStartTime,
		sessions,
	};
}
